import { useBoxRenewalError } from './useBoxRenewalError';
import { CustomQuestionsForm, questionErrors, useCustomQuestions } from './CustomQuestions';
import {
  usernameValidation,
  phoneNumberOptional,
  isBuiltinQuestion,
  type CustomAnswer,
} from '@lub0v/santa-utils';
import { ParticipantPicturePicker } from '@src/components/PicturePicker/ParticipantPicturePicker';
import { useWishHint } from './useWishHint';
import {
  normalizeWishLinks,
  normalizeCompletedWishLinks,
} from '@src/components/WishText/formattedLinks';
import { useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, FullCardInfo } from '@lub0v/santa-utils';
import {
  Button,
  CreationShell,
  HintError,
  Input,
  Textarea,
  Stack,
  Divider,
  Typography,
} from '@src/components';
import { useMatchesMedia, MOBILE_HEADER_MQ } from '@src/components/layout/useMobileHeaderRecede';
import { NewBoxEmailStep } from '@src/pages/NewBoxPage/NewBoxEmailStep';
import { AccessCodes } from '@src/pages/BoxPage/AccessCodes';

import { boxExchangePath } from '@src/routing/routes';
import { santaClient } from '@src/state/client';
import { useAppDispatch, useAppSelector } from '@src/state/hooks';
import { loadSession } from '@src/state/sessionSlice';

export interface CreateCardWizardProps {
  info: BoxInfo;
  card: FullCardInfo | null;
  join: string;
  onChanged: () => Promise<void> | void;
  onCancel?: () => void;
  onComplete?: () => void;
}

type CardDraft = {
  username: string;
  picture: string;
  avatar: string;
  wish: string;
  postalAddress: string;
  postalCode: string;
  postalRecipient: string;
  phone: string;
};

export function CreateCardWizard({
  info,
  card,
  join,
  onChanged,
  onCancel,
  onComplete,
}: CreateCardWizardProps) {
  const navigate = useNavigate();
  const mobile = useMatchesMedia(MOBILE_HEADER_MQ);
  const { t } = useTranslation();
  const wishHint = useWishHint(info.box, false);
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.session.user);
  // Keep the step list stable while email verification refreshes the session.
  const [askEmail] = useState(() => !user?.email || user.isGuest);
  const [email, setEmail] = useState('');
  const joined = useRef(false);
  const questions = useCustomQuestions(info.box.key, join);
  const typedQuestionnaire = questions.fields.some(isBuiltinQuestion);
  const [answers, setAnswers] = useState<CustomAnswer[]>(() =>
    (card?.customAnswers ?? []).map(({ fieldId, value }) => ({ fieldId, value })),
  );
  const [answerErrors, setAnswerErrors] = useState<Record<string, string>>({});
  const steps = ['questionnaire', 'picture', ...(askEmail ? ['email'] : [])] as const;
  const [index, setIndex] = useState(0);
  const [draft, setDraft] = useState<CardDraft>(() => ({
    username: card?.username || '',
    // A server-assigned avatar is not a choice made in this wizard.
    picture: '',
    avatar: card?.avatar || '',
    wish: normalizeWishLinks(card?.wish || '', t('game.card.wishLinkLabel')),
    postalAddress: card?.postalAddress || '',
    postalCode: card?.postalCode || '',
    postalRecipient: card?.postalRecipient || '',
    phone: card?.phone || '',
  }));
  const handleRenewalError = useBoxRenewalError(info);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(card?.recoveryCodes || null);
  const [cardUrl, setCardUrl] = useState(card?.cardUrl || '');
  const step = steps[index];
  const lastStep = index >= steps.length - 1;

  const [touched, setTouched] = useState<Partial<Record<keyof CardDraft, boolean>>>({});
  const draftErrors: Partial<Record<keyof CardDraft, string>> = {};
  if (!usernameValidation.isValidSync(draft.username.trim()))
    draftErrors.username = 'game.cardCreate.validation.name';
  for (const [key, limit] of Object.entries({
    wish: 32768,
    postalAddress: 256,
    postalRecipient: 256,
    postalCode: 12,
  }) as [keyof CardDraft, number][]) {
    const shown = !typedQuestionnaire && (key === 'wish' ? info.box.useWish : info.box.usePost);
    if (shown && draft[key].length > limit)
      draftErrors[key] = t('game.cardCreate.validation.length', { max: limit });
  }
  if (!typedQuestionnaire && info.box.usePost) {
    for (const key of ['postalAddress', 'postalRecipient'] as const)
      if (!draft[key].trim()) draftErrors[key] = 'questions.requiredAnswer';
  }
  if (!typedQuestionnaire && (info.box.usePost || info.box.isPhoneRequired)) {
    if (info.box.isPhoneRequired && !draft.phone.trim())
      draftErrors.phone = 'questions.requiredAnswer';
    else if (!phoneNumberOptional.isValidSync(draft.phone.trim()))
      draftErrors.phone = 'game.cardCreate.validation.phone';
  }
  const currentAnswerErrors = questionErrors(
    questions.fields,
    answers,
    card?.needsCardSetup === false ? (card.customAnswers ?? []) : undefined,
  );
  const questionnaireValid =
    !questions.loading &&
    !questions.error &&
    Object.keys(draftErrors).length === 0 &&
    Object.keys(currentAnswerErrors).length === 0;
  const fieldError = (key: keyof CardDraft) =>
    (touched[key] || draft[key].length > 0) && draftErrors[key] ? t(draftErrors[key]!) : undefined;
  const touch = (key: keyof CardDraft) => setTouched((previous) => ({ ...previous, [key]: true }));

  function patchDraft(fields: Partial<CardDraft>) {
    setDraft((current) => ({ ...current, ...fields }));
  }

  async function ensureGuestSession() {
    if (!join || card || joined.current) return true;
    const result = await santaClient.joinGuest({ key: info.box.key, join });
    if (!result.ok) {
      if (!handleRenewalError(result.error?.message)) setError(result.error?.message || 'box.notFound');
      return false;
    }
    joined.current = true;
    await dispatch(loadSession());
    return true;
  }

  async function goToStep(nextIndex: number) {
    if (busy) return;
    setError(null);
    if (steps[nextIndex] === 'email') {
      setBusy(true);
      try {
        if (!(await ensureGuestSession())) return;
      } finally {
        setBusy(false);
      }
    }
    setIndex(nextIndex);
  }

  const savingCard = useRef(false);
  async function finishWizard(nextDraft = draft) {
    if (savingCard.current || questions.loading || questions.error) return;
    if (!questionnaireValid) {
      setTouched({
        username: true,
        wish: true,
        postalAddress: true,
        postalRecipient: true,
        postalCode: true,
        phone: true,
      });
      setAnswerErrors(currentAnswerErrors);
      setIndex(0);
      return;
    }
    const errors = questionErrors(
      questions.fields,
      answers,
      card?.needsCardSetup === false ? (card.customAnswers ?? []) : undefined,
    );
    setAnswerErrors(errors);
    if (Object.keys(errors).length) {
      setIndex(0);
      return;
    }
    savingCard.current = true;
    try {
      setBusy(true);
      setError(null);
      if (!(await ensureGuestSession())) {
        return;
      }
      const res = await santaClient.saveCard({
        key: info.box.key,
        join,
        username: nextDraft.username.trim(),
        picture: nextDraft.picture,
        avatar: nextDraft.avatar,
        wish: normalizeWishLinks(nextDraft.wish, t('game.card.wishLinkLabel')),
        postalAddress: nextDraft.postalAddress,
        postalCode: nextDraft.postalCode,
        postalRecipient: nextDraft.postalRecipient,
        phone: nextDraft.phone,
        customAnswers: answers,
      });
      if (!res.ok || !res.data) {
        if (!handleRenewalError(res.error?.message)) setError(t(res.error?.message || 'newBox.saveFailed'));
        return;
      }
      if (res.data.recoveryCodes?.length) {
        // Membership must be fresh before the visitor can acknowledge codes and
        // leave the wizard, otherwise the exchange guard still sees a non-member.
        await onChanged();
        setCodes(res.data.recoveryCodes);
        setCardUrl(res.data.cardUrl || '');
        return;
      }
      await onChanged();
      onComplete?.();
      navigate(boxExchangePath(info.box.key));
    } catch {
      setError(t('newBox.saveFailed'));
    } finally {
      savingCard.current = false;
      setBusy(false);
    }
  }

  function continueStep() {
    if (
      uploadingPicture ||
      (step === 'questionnaire' && !questionnaireValid) ||
      (step === 'picture' && !draft.picture)
    ) {
      return;
    }
    if (step === 'questionnaire') {
      const errors = questionErrors(
        questions.fields,
        answers,
        card?.needsCardSetup === false ? (card.customAnswers ?? []) : undefined,
      );
      setAnswerErrors(errors);
      if (Object.keys(errors).length) return;
    }
    if (lastStep) {
      void finishWizard();
      return;
    }
    goToStep(index + 1);
  }

  function pickPicture(picture: string, avatar: string) {
    if (busy) {
      return;
    }
    const nextDraft = { ...draft, picture, avatar };
    setDraft(nextDraft);
    if (mobile) return;
    if (lastStep) {
      void finishWizard(nextDraft);
      return;
    }
    goToStep(index + 1);
  }

  function goBack() {
    if (index > 0) {
      goToStep(index - 1);
      return;
    }
    onCancel?.();
    navigate('/');
  }

  if (questions.loading || questions.error)
    return (
      <Stack gap={3}>
        {questions.loading ? (
          <p role="status">{t('questions.loading')}</p>
        ) : (
          <>
            <HintError error={t(questions.error!)} />
            <Button onClick={questions.retry}>{t('questions.retry')}</Button>
          </>
        )}
      </Stack>
    );

  if (codes?.length) {
    return (
      <AccessCodes
        kind="participant"
        subject={draft.username}
        url={cardUrl}
        codes={codes}
        onContinue={async () => {
          setCodes(null);
          await dispatch(loadSession());
          onComplete?.();
          navigate(boxExchangePath(info.box.key));
        }}
      />
    );
  }

  if (step === 'email') {
    return (
      <NewBoxEmailStep
        testId="create-card"
        disabled={busy}
        subtitle={t('game.cardCreate.email.subtitle')}
        alreadyUsedKey="game.cardCreate.email.alreadyUsed"
        email={email}
        onEmailChange={setEmail}
        onComplete={() => {
          if (!busy) {
            if (lastStep) void finishWizard();
            else void goToStep(index + 1);
          }
        }}
        shell={(title, subtitle, children, action) => (
          <CreationShell
            showHeader={false}
            mobileFocused={mobile}
            stepKey="email"
            footer={!mobile}
            heading={t('newBox.email.label')}
            title={title}
            subtitle={subtitle}
            step={index + 1}
            steps={steps.length}
            onBack={() => {
              if (!busy && !uploadingPicture) goBack();
            }}
            error={<HintError error={error} testId="create-card-error" />}
            action={action}
            testId="create-card"
          >
            {children}
          </CreationShell>
        )}
      />
    );
  }

  const showContinue = mobile || step !== 'picture';

  return (
    <CreationShell
      showHeader={false}
      mobileFocused={mobile}
      mobileBare={step === 'picture'}
      stepKey={step}
      footer={!mobile}
      heading={t(`game.cardCreate.${step}.header`)}
      title={t(`game.cardCreate.${step}.title`)}
      subtitle={t(`game.cardCreate.${step}.subtitle`)}
      step={index + 1}
      steps={steps.length}
      onBack={() => {
        if (!busy && !uploadingPicture) goBack();
      }}
      error={<HintError error={error} testId="create-card-error" />}
      action={
        showContinue ? (
          <Button
            variant="primary"
            loading={busy}
            disabled={
              uploadingPicture ||
              (step === 'questionnaire' && !questionnaireValid) ||
              (step === 'picture' && !draft.picture)
            }
            onClick={() => continueStep()}
            testId="create-card-next"
          >
            {lastStep ? t('game.cardCreate.done') : t('newBox.continue')}
          </Button>
        ) : undefined
      }
      testId="create-card"
    >
      {step === 'questionnaire' && (
        <Stack gap={6}>
          <fieldset className="custom-question" disabled={busy}>
            <legend>{t('game.cardCreate.name.label')}</legend>
            <Typography as="p" role="meta/helper">
              {t('game.cardCreate.name.hint')}
            </Typography>
            <Input
              className="card-questionnaire__name"
              value={draft.username}
              onValueChange={(username) => patchDraft({ username })}
              aria-label={t('game.cardCreate.name.label')}
              error={fieldError('username')}
              onBlur={() => touch('username')}
              maxLength={64}
              testId="create-card-name"
            />
          </fieldset>
          {questions.fields.length > 0 && <Divider />}
          <CustomQuestionsForm
            showHeading={false}
            wishHint={wishHint}
            testIdPrefix="create-card"
            fields={questions.fields}
            answers={answers}
            onChange={(nextAnswers) => {
              setAnswers(nextAnswers);
              setAnswerErrors({});
            }}
            errors={answerErrors}
            disabled={busy}
          />
          {!typedQuestionnaire && info.box.useWish && (
            <>
              <Divider />
              <fieldset className="custom-question" disabled={busy}>
                <legend>{t('game.cardCreate.wish.header')}</legend>
                <Typography as="p" role="meta/helper">
                  {wishHint}
                </Typography>
                <Textarea
                  value={draft.wish}
                  onValueChange={(wish) =>
                    patchDraft({
                      wish: normalizeCompletedWishLinks(wish, t('game.card.wishLinkLabel')),
                    })
                  }
                  onBlur={() =>
                    patchDraft({
                      wish: normalizeWishLinks(draft.wish, t('game.card.wishLinkLabel')),
                    })
                  }
                  aria-label={t('game.cardCreate.wish.label')}
                  error={fieldError('wish')}
                  maxLength={32768}
                  rows={5}
                  testId="create-card-wish"
                />
              </fieldset>
            </>
          )}
          {!typedQuestionnaire && info.box.usePost && (
            <>
              <Divider />
              <fieldset className="custom-question" disabled={busy}>
                <legend>{t('game.cardCreate.delivery.sectionTitle')}</legend>
                <Typography as="p" role="meta/helper">
                  {t('questions.visibility.santa')}
                </Typography>
                <Input
                  name="postalRecipient"
                  maxLength={256}
                  error={fieldError('postalRecipient')}
                  onBlur={() => touch('postalRecipient')}
                  autoComplete="shipping name"
                  value={draft.postalRecipient}
                  onValueChange={(postalRecipient) => patchDraft({ postalRecipient })}
                  label={t('game.cardCreate.delivery.recipient')}
                />
                <div className="field-address-row">
                  <Input
                    name="postalAddress"
                    maxLength={256}
                    error={fieldError('postalAddress')}
                    onBlur={() => touch('postalAddress')}
                    autoComplete="shipping street-address"
                    value={draft.postalAddress}
                    onValueChange={(postalAddress) => patchDraft({ postalAddress })}
                    label={t('game.cardCreate.delivery.address')}
                  />
                  <Input
                    name="postalCode"
                    error={fieldError('postalCode')}
                    onBlur={() => touch('postalCode')}
                    autoComplete="shipping postal-code"
                    value={draft.postalCode}
                    onValueChange={(postalCode) => patchDraft({ postalCode })}
                    label={t('game.cardCreate.delivery.code')}
                    maxLength={12}
                  />
                </div>
              </fieldset>
            </>
          )}
          {!typedQuestionnaire && (info.box.usePost || info.box.isPhoneRequired) && (
            <fieldset className="custom-question" disabled={busy}>
              <legend>{t('game.cardCreate.delivery.phone')}</legend>
              <Typography as="p" role="meta/helper">
                {t('questions.visibility.santa')}
              </Typography>
              <Input
                name="phone"
                maxLength={32}
                error={fieldError('phone')}
                onBlur={() => touch('phone')}
                autoComplete="shipping tel"
                type="tel"
                value={draft.phone}
                onValueChange={(phone) => patchDraft({ phone })}
                aria-label={t('game.cardCreate.delivery.phone')}
              />
            </fieldset>
          )}
        </Stack>
      )}
      {step === 'picture' ? (
        <ParticipantPicturePicker
          value={draft.picture}
          avatar={draft.avatar}
          onUploadingChange={setUploadingPicture}
          disabled={busy}
          onValueChange={pickPicture}
          testId="create-card-picture"
        />
      ) : null}
    </CreationShell>
  );
}
